import React, { useEffect, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { faqs, testimonials as fallback } from '../../mock';
import { Star, Quote, MessageSquarePlus } from 'lucide-react';
import { fetchTestimonials } from '../../lib/api';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';

const FAQ = () => {
  const { t } = useI18n();
  const [list, setList] = useState([]);

  useEffect(() => {
    fetchTestimonials()
      .then((res) => {
        if (Array.isArray(res) && res.length > 0) {
          setList(res.map((t) => ({
            name: t.name,
            role: [t.role, t.company, t.country].filter(Boolean).join(', ') || 'Client',
            quote: t.quote,
            avatar: t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=0f172a&color=fff`,
            rating: t.rating || 5,
            logo: t.logo,
          })));
        } else {
          setList(fallback);
        }
      })
      .catch(() => setList(fallback));
  }, []);

  return (
    <section className="py-20 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">{t('faq.kicker')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t('faq.title')}</h2>
          <p className="mt-4 text-slate-600">{t('faq.body')}</p>

          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-2">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="px-4">
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-orange-600">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-sm leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">{t('faq.clientKicker')}</span>
              <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900">{t('faq.clientTitle')}</h3>
            </div>
            <Link to="/submit-testimonial" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-400 rounded-full px-3 py-1.5 bg-orange-50">
              <MessageSquarePlus className="h-3.5 w-3.5" /> {t('faq.shareYours')}
            </Link>
          </div>

          <div className="mt-6 space-y-4 max-h-[720px] overflow-y-auto pr-1">
            {list.map((tm, i) => (
              <div key={tm.name + i} className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-lg transition-shadow">
                <Quote className="h-6 w-6 text-orange-500" />
                <p className="mt-2 text-slate-700 text-sm leading-relaxed">“{tm.quote}”</p>
                <div className="mt-4 flex items-center gap-3">
                  <img src={tm.avatar} alt={tm.name} className="h-10 w-10 rounded-full object-cover bg-slate-100" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{tm.name}</div>
                    <div className="text-xs text-slate-500">{tm.role}</div>
                  </div>
                  <div className="ml-auto flex text-amber-400">
                    {[...Array(tm.rating || 5)].map((_, i) => (<Star key={i} className="h-4 w-4 fill-current" />))}
                  </div>
                </div>
                {tm.logo && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-400">Company</span>
                    <img src={tm.logo} alt="logo" className="h-6 max-w-[120px] object-contain" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
